/**
 * UTM + Reddit Click ID attribution persistence.
 *
 * - Captures URL params on every page load.
 * - Last-touch stored in localStorage under `wcc_attribution` (30-day TTL).
 * - First-touch stored once under `wcc_attribution_first`.
 * - Registers attribution super-properties with PostHog so they ride along
 *   with every subsequent event, and fires a one-per-session
 *   `attribution_captured` event.
 */

const STORAGE_KEY = "wcc_attribution";
const FIRST_TOUCH_KEY = "wcc_attribution_first";
const SESSION_FLAG = "wcc_attribution_sent";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const TRACKED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "rdt_cid",
] as const;

export type AttributionData = Partial<Record<(typeof TRACKED_PARAMS)[number], string>>;

interface StoredAttribution {
  data: AttributionData;
  ts: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readStored(key: string): AttributionData | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: StoredAttribution = JSON.parse(raw);
    if (Date.now() - parsed.ts > TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writeStored(key: string, data: AttributionData): void {
  const entry: StoredAttribution = { data, ts: Date.now() };
  localStorage.setItem(key, JSON.stringify(entry));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Parse tracked params from the current URL. Returns null if none present. */
function parseParams(): AttributionData | null {
  const url = new URL(window.location.href);
  const result: AttributionData = {};
  let found = false;

  for (const key of TRACKED_PARAMS) {
    const val = url.searchParams.get(key);
    if (val) {
      result[key] = val;
      found = true;
    }
  }

  return found ? result : null;
}

/**
 * Call once on every page load (e.g. in main.tsx).
 *
 * 1. Captures URL params and persists last-touch / first-touch.
 * 2. Registers PostHog super-properties.
 * 3. Fires `attribution_captured` once per browser session.
 */
export function captureAttribution(): void {
  const incoming = parseParams();

  if (incoming) {
    // Last-touch: merge with existing, latest non-empty value wins
    const existing = readStored(STORAGE_KEY) ?? {};
    const merged: AttributionData = { ...existing, ...incoming };
    writeStored(STORAGE_KEY, merged);

    // First-touch: set only if not already present
    if (!readStored(FIRST_TOUCH_KEY)) {
      writeStored(FIRST_TOUCH_KEY, incoming);
    }
  }

  // Register with PostHog (even if no new params - covers returning visitors)
  const attribution = readStored(STORAGE_KEY);
  if (!attribution) return;

  const posthog = (window as any).posthog;
  if (!posthog) return;

  posthog.register(attribution);

  // Fire once per browser session
  if (!sessionStorage.getItem(SESSION_FLAG)) {
    posthog.capture("attribution_captured", attribution);
    sessionStorage.setItem(SESSION_FLAG, "1");
  }
}

/** Return the current last-touch attribution payload (or null). */
export function getAttribution(): AttributionData | null {
  return readStored(STORAGE_KEY);
}

/** Return the first-touch attribution payload (or null). */
export function getFirstAttribution(): AttributionData | null {
  return readStored(FIRST_TOUCH_KEY);
}
